import React, {useContext, useEffect, useRef, useState} from "react";
import { Button, Form, Table } from "react-bootstrap";
import { orderAffiliatePayOutApi, getUserAffiliatePayOutOrdersApi } from "../../http/affiliateAPI";
import { Context } from "../../index";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";

const AffiliatePayOutOrders = observer(() => {
    const { user, affiliateStore } = useContext(Context);
    const [errors, setErrors] = useState({});
    const emailValidationTimeout = useRef(null);



    const [form, setForm] = useState({
        requested_amount: "",
        payment_method: "Main_balance",
        payment_details: "",
        order_comment: ""
    });

    const paymentMethods = [
        { value: "Main_balance", label: "Main Balance" },
        { value: "Payoneer", label: "Payoneer" }
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value.trim();

        if (name === "payment_details") {
            processedValue = processedValue.toLowerCase();

            // Очищаем предыдущий таймер, если пользователь печатает
            if (emailValidationTimeout.current) {
                clearTimeout(emailValidationTimeout.current);
            }

            // Устанавливаем новый таймер на 3 секунды
            emailValidationTimeout.current = setTimeout(() => {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(processedValue)) {
                    setErrors((prev) => ({ ...prev, [name]: "Invalid email format" }));
                } else {
                    setErrors((prev) => ({ ...prev, [name]: null }));
                }
            }, 3000);
        }

        setForm((prev) => ({
            ...prev,
            [name]: processedValue
        }));
    };


    useEffect(() => {
        return () => {
            if (emailValidationTimeout.current) {
                clearTimeout(emailValidationTimeout.current);
            }
        };
    }, []);


    const handleSubmit = async () => {
        const amount = parseFloat(form.requested_amount);
        if (isNaN(amount) || amount < 50) {
            return toast.error("Minimum payout amount is $50.00", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        }

        try {
            const response = await orderAffiliatePayOutApi({
                userId: user.user.id,
                requested_amount: amount,
                payment_method: form.payment_method,
                payment_details: form.payment_details,
                order_comment: form.order_comment
            });

            toast.info(response.message || "Request submitted!", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });

            const updated = await getUserAffiliatePayOutOrdersApi(user.user.id);
            affiliateStore.setUserPayouts(updated);
            setForm({
                requested_amount: "",
                payment_method: "Main_balance",
                payment_details: "Enter Payoneer Email",
                order_comment: ""
            });
        } catch (e) {
            toast.error(e.response?.data?.message || "Failed to submit payout request!", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        }
    };

    useEffect(() => {
        getUserAffiliatePayOutOrdersApi(user.user.id).then(data => {
            affiliateStore.setUserPayouts(data);
        });
    }, [user.user.id, affiliateStore]);

    return (
        <div className="p-3 rounded">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
                <h6 className="text-success mb-1">Request Affiliate Payout</h6>
                <h6 className="text-muted small mb-0">
                    Affiliate Balance: <strong>${user.user.affiliate_balance?.toFixed(2)}</strong>
                </h6>
            </div>

            <div className="text-muted small mb-3">
                <p>
                    To request an affiliate payout, please carefully fill out the form below.
                    Make sure all fields are accurate to avoid delays.
                </p>

                <p>
                    <strong>Amount</strong> – specify the exact amount you wish to withdraw.
                    The minimum payout amount is <strong>$50.00</strong>. If your balance is below this,
                    you will need to accumulate more funds before submitting a request.
                </p>

                <p>
                    <strong>Payment Method</strong> – choose how you'd like to receive your payout.
                    You can either:
                </p>

                <ul className="mb-2">
                    <li>
                        <em>Transfer to Main Balance</em> – funds will be credited to your internal balance on the platform.
                    </li>
                    <li>
                        <em>Payoneer</em> – funds will be sent to your Payoneer account.
                    </li>
                </ul>

                <p>
                    <strong>Payment Details</strong> – enter the necessary information based on the selected method.
                    For Payoneer, this should be the email linked to your Payoneer account.
                    If you're transferring to your main balance, this field can be left empty.
                </p>

                <p>
                    <strong>Comment</strong> – this field is optional.
                    You can leave a short note or clarification for the administrator who will process your request.
                    For example: "Please process before Friday" or "Send invoice for accounting".
                </p>

                <p>
                    Once submitted, your request will be reviewed by the Virtalibry team.
                    Please allow up to 48 hours for processing. You will be notified once your request is approved
                    or if any additional information is needed.
                </p>
            </div>


            <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <div className="d-flex flex-column flex-md-row gap-3 mb-3">
                    <div>
                        <Form.Label className="small text-muted mb-1">Amount</Form.Label>
                        <Form.Control
                            size="sm"
                            className="text-success"
                            type="number"
                            step="0.01"
                            min="0"
                            name="requested_amount"
                            value={form.requested_amount}
                            onChange={handleChange}
                            placeholder="Amount"
                            required
                            style={{ minWidth: "100px", maxWidth: "150px" }}
                        />
                    </div>

                    <div>
                        <Form.Label className="small text-muted mb-1">Payment Method</Form.Label>
                        <Form.Select
                            size="sm"
                            className="text-success"
                            name="payment_method"
                            value={form.payment_method}
                            onChange={handleChange}
                            style={{ minWidth: "140px", maxWidth: "180px" }}
                        >
                            {paymentMethods.map((method) => (
                                <option key={method.value} value={method.value}>
                                    {method.label}
                                </option>
                            ))}
                        </Form.Select>
                    </div>

                    {form.payment_method === "Payoneer" && (
                        <div>
                            <Form.Label className="small text-muted mb-1">Payoneer Email</Form.Label>
                            <Form.Control
                                size="sm"
                                className="text-success"
                                name="payment_details"
                                value={form.payment_details}
                                onChange={handleChange}
                                placeholder="Enter Payoneer Email"
                                required
                                isInvalid={!!errors.payment_details}
                                style={{ minWidth: "160px", maxWidth: "200px" }}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.payment_details}
                            </Form.Control.Feedback>
                        </div>
                    )}

                    <div className="flex-grow-1">
                        <Form.Label className="small text-muted mb-1">Comment</Form.Label>
                        <Form.Control
                            size="sm"
                            className="text-success"
                            name="order_comment"
                            value={form.order_comment}
                            onChange={handleChange}
                            placeholder="Optional Comment"
                        />
                    </div>

                    <div className="d-flex flex-column align-self-start align-self-md-end">
                        <Form.Label className="invisible mb-1">Save</Form.Label>
                        <Button type="submit" size="sm" variant="success" style={{ maxWidth: "140px" }}>
                            Order Pay Out
                        </Button>
                    </div>
                </div>
            </Form>

            <h6 className="text-muted small mb-2">Your Payout Requests</h6>

            <div className="table-responsive">
                <Table striped bordered hover size="sm" className="w-100 small text-muted fst-italic mb-0" style={{  lineHeight: '14px' }}>
                <thead>
                    <tr className="text-nowrap text-muted">
                        <th className="text-muted">#</th>
                        <th className="text-muted">Amount</th>
                        <th className="text-muted">Method</th>
                        <th className="text-muted">Details</th>
                        <th className="text-muted text-center">Balance Before</th>
                        <th className="text-muted text-center">Balance After</th>
                        <th className="text-muted">Comment</th>
                        <th className="text-muted text-center">Status</th>
                        <th className="text-muted">Requested At</th>
                    </tr>
                    </thead>
                    <tbody>
                    {affiliateStore.userPayouts.map((item, idx) => (
                        <tr key={item.id || idx} className="text-nowrap small text-muted">
                            <td className="text-center">{idx + 1}</td>
                            <td className="text-center">${parseFloat(item.requested_amount).toFixed(2)}</td>
                            <td>{item.payment_method}</td>
                            <td>{item.payment_details}</td>
                            <td className="text-center">${parseFloat(item.affiliate_balance_before).toFixed(2)}</td>
                            <td className="text-center">${parseFloat(item.affiliate_balance_after).toFixed(2)}</td>
                            <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {item.order_comment || "-"}
                            </td>
                            <td className="text-center">
              <span className={`badge bg-${item.order_status === "approved" ? "success" : item.order_status === "rejected" ? "danger" : "secondary"}`}>
                {item.order_status}
              </span>
                            </td>
                            <td>
                                {(() => {
                                    const d = new Date(item.requested_date);
                                    const day = String(d.getDate()).padStart(2, '0');
                                    const month = d.toLocaleString('en-US', { month: 'short' });
                                    const year = d.getFullYear();
                                    const hours = String(d.getHours()).padStart(2, '0');
                                    const mins = String(d.getMinutes()).padStart(2, '0');
                                    return `${day}-${month}-${year} ${hours}:${mins}`;
                                })()}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            </div>
        </div>

    );
});

export default AffiliatePayOutOrders;
